import React, { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { FirstTimeModalTypes } from '@/types/userConfigs';
import { firstTimeModalConfigAtom, userConfigsAtom } from '@/atoms/queries/userConfigs';
import { useUpsertUserConfig } from '@/hooks/useUpsertUserConfig';

export const FirstTimeModalWithData = ({
  modalType,
  children,
}: {
  modalType: FirstTimeModalTypes;
  children: React.ReactNode;
}) => {
  const userConfigs = useAtomValue(userConfigsAtom);
  const firstTimeModalConfig = useAtomValue(firstTimeModalConfigAtom);
  const upsertUserConfig = useUpsertUserConfig({ shouldRefetch: false });
  const userHasSeenModal = firstTimeModalConfig?.definition?.[modalType];

  // Empty dependency array in order to run exactly once on mount (twice in dev because of strict mode) so that the
  // user only has to see (not acknowledge) the modal once.
  useEffect(() => {
    if (userHasSeenModal) return () => {}; // see unmount
    const { configId, definition } = firstTimeModalConfig || {};

    // fire-and-forget
    upsertUserConfig({
      configId,
      configDefinition: { ...definition, [modalType]: true },
      configType: 'firstTimeModal',
    });

    // on unmount, re-fetch user configs to ensure we have the latest state. Without this we may end up with multiple
    // copies of the user config in the db. The first time the user loads the app they will have no `firstTimeModal`
    // config so it'll create one. If you don't re-fetch then it'll still think you don't have one so it'll make another
    return () => {
      userConfigs.refetch();
    };
  }, []);

  return userHasSeenModal ? null : children;
};
